import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { bsMonthName } from "@/lib/bs-calendar";
import { Sparkles } from "lucide-react";

/**
 * Phase 6.1.D — Self-promoter view of eligible (approved + active) sell-out schemes.
 * Shows scheme rules and the entry incentive value. Their own qualification appears
 * via the sell-out scheme ledger (scoped to their dealer).
 */
export default function PromoterEligibleSchemes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/promoter/schemes");
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error("Could not load schemes", { description: formatApiErrorDetail(e.response?.data?.detail) });
      } finally { setLoading(false); }
    })();
  }, []);
  return (
    <div className="space-y-4 max-w-3xl" data-testid="my-schemes-page">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">My Eligible Schemes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Promotional schemes you can earn entry incentive on. Your dealer also receives
          a benefit when the qualification threshold is met.
        </p>
      </header>
      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <Card><CardContent className="p-6 text-center text-slate-500" data-testid="my-schemes-empty">
          No active schemes for you right now.
        </CardContent></Card>
      )}
      <div className="space-y-3">
        {rows.map((s) => (
          <Card key={s.id} data-testid={`my-scheme-${s.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start gap-3">
                <Sparkles size={18} className="text-amber-500 mt-1" />
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{s.name}</div>
                  {s.description && <div className="text-sm text-slate-600 mt-1">{s.description}</div>}
                </div>
                <span className="text-xs text-slate-500">{period(s)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-slate-100">
                <Detail label="Qualifies on" value={qualText(s)} />
                <Detail label="Eligible" value={`${s.eligible_brand || "Any brand"} / ${s.eligible_category || "Any category"}`} />
                <Detail label="Your entry incentive" value={`Rs. ${Number(s.self_promoter_entry_incentive || 0).toLocaleString()}`} highlight />
                <Detail label="Calculated on" value="Net sales (excl. 13% VAT)" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function period(s) {
  if (s.period_kind === "bs_month") {
    const [y, m] = String(s.bs_month_year || "").split("-");
    return `${bsMonthName(Number(m))} ${y} (BS)`;
  }
  if (s.period_kind === "bs_quarter") return s.bs_quarter_key;
  if (s.period_kind === "bs_range") return `${s.bs_from} → ${s.bs_to}`;
  return "—";
}
function qualText(s) {
  if (s.qualification_kind === "min_units") return `≥ ${s.min_units} units`;
  return `≥ Rs. ${Number(s.min_net_sales || 0).toLocaleString()} (net)`;
}
function Detail({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`text-sm ${highlight ? "font-semibold text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
